import { useCallback, useEffect, useRef, useState } from 'react'
import type { ethers } from 'ethers'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { reportClientError } from 'utils/reportClientError'
import { HUB_TOKENS } from 'utils/swap/addresses'
import { clearDiscoveryCache, discoverPools, filterPoolsForBurn, readMproBurnBps } from 'utils/swap/discovery'
import { fetchKyberQuote } from 'utils/swap/kyber'
import { quoteRoutes } from 'utils/swap/quote'
import { feeFor, isRouterTarget, readAllowedVenues, readRouterFee } from 'utils/swap/router'
import { rankQuotes } from 'utils/swap/rank'
import { sameChainToken, toChainToken } from 'utils/swap/tokens'
import type { Quote, RouteCandidate, SwapToken } from 'utils/swap/types'

const DEBOUNCE_MS = 400
const REFRESH_MS = 15_000
const HUBS = HUB_TOKENS.map((h) => h.address)

/** Reported once per venue per session — it is a configuration fact, not an event. */
const reportedUnroutable = new Set<string>()

export type QuoteError = 'none' | 'noRoute' | 'unavailable'

export interface UseSwapQuoteResult {
  quotes: Quote[]
  best: Quote | null
  loading: boolean
  error: QuoteError
  /** MPRO's burn for this account, in basis points. Zero unless MPRO is involved. */
  burnBps: number
  /** The platform router's fee for this account, in basis points; 0 without a router. */
  feeBps: number
  /** When the shown quotes were produced, so the panel can call them stale. */
  quotedAt: number | null
  refresh: () => void
}

/**
 * Keeps the route list in step with what the user has typed.
 *
 * The whole hook is built around one hazard: a quote is a promise about a price
 * that is already moving, and two of them in flight will land in whatever order
 * the network decides. So every run carries a sequence number and a result from
 * anything but the newest run is dropped rather than rendered — otherwise a
 * slow answer for an old amount overwrites a fast answer for the current one,
 * and the number on screen belongs to a trade nobody asked for.
 */
export function useSwapQuote(params: {
  chainId: number
  tokenIn: SwapToken | null
  tokenOut: SwapToken | null
  amountIn: bigint
  account?: string | null
  mproAddress: string | null
  masterDistributorAddress: string
  /** The platform router (scope 16); null = quote and trade direct, no fee. */
  routerAddress?: string | null
  enabled?: boolean
}): UseSwapQuoteResult {
  const {
    chainId,
    tokenIn,
    tokenOut,
    amountIn,
    account,
    mproAddress,
    masterDistributorAddress,
    routerAddress = null,
    enabled = true
  } = params

  const [quotes, setQuotes] = useState<Quote[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<QuoteError>('none')
  const [burnBps, setBurnBps] = useState(0)
  const [feeBps, setFeeBps] = useState(0)
  const [quotedAt, setQuotedAt] = useState<number | null>(null)
  const [nonce, setNonce] = useState(0)

  const sequence = useRef(0)
  const inFlight = useRef<AbortController | null>(null)

  const refresh = useCallback(() => {
    clearDiscoveryCache()
    setNonce((n) => n + 1)
  }, [])

  const inAddress = tokenIn?.address ?? null
  const outAddress = tokenOut?.address ?? null

  useEffect(() => {
    if (!enabled || !tokenIn || !tokenOut || amountIn <= 0n || sameChainToken(tokenIn, tokenOut)) {
      inFlight.current?.abort()
      sequence.current += 1
      setQuotes([])
      setLoading(false)
      setError('none')
      setQuotedAt(null)
      return undefined
    }

    let cancelled = false
    const run = async () => {
      inFlight.current?.abort()
      const controller = new AbortController()
      inFlight.current = controller
      const mine = ++sequence.current
      setLoading(true)

      try {
        const provider = getSimpleRpcProviderByChainId(chainId) as ethers.providers.Provider
        const chainIn = toChainToken(tokenIn)
        const chainOut = toChainToken(tokenOut)

        /*
         * MPRO's burn decides which venues can deliver it at all, so it is read
         * before the pools are filtered rather than discovered afterwards from
         * a revert. Per account, because a whitelisted sender burns nothing
         * whatever the global rate says.
         */
        const involvesMpro = Boolean(tokenIn.isMpro || tokenOut.isMpro)
        const burn = involvesMpro ? await readMproBurnBps(provider, masterDistributorAddress, account) : 0
        if (cancelled || mine !== sequence.current) return
        setBurnBps(burn)

        /*
         * The platform fee comes off the input before any venue sees it, so
         * the venues are quoted for the NET amount and the screen shows what
         * the trade will actually return. The gross amount travels on the
         * quote for the transaction and the fee row.
         */
        const routerFee = routerAddress ? await readRouterFee(provider, routerAddress, account) : null
        if (cancelled || mine !== sequence.current) return
        const effectiveFeeBps = routerFee && !routerFee.exempt ? routerFee.feeBps : 0
        setFeeBps(effectiveFeeBps)
        const feeIn = feeFor(amountIn, effectiveFeeBps)
        const netAmountIn = amountIn - feeIn
        if (netAmountIn <= 0n) {
          setQuotes([])
          setError('noRoute')
          return
        }

        const discovered = await discoverPools({
          provider,
          chainId,
          tokenIn: chainIn,
          tokenOut: chainOut,
          hubs: HUBS
        })
        if (cancelled || mine !== sequence.current) return
        const pools = filterPoolsForBurn(discovered, mproAddress, burn)

        /*
         * The aggregator is asked alongside our own routes, not after them: it
         * is one more opinion, and waiting for it would make every quote as
         * slow as the slowest party. It resolves to null on any trouble, so a
         * gateway that is down costs one row and nothing else.
         */
        const [onChain, kyber] = await Promise.all([
          quoteRoutes({
            provider,
            pools,
            tokenIn: chainIn.address,
            tokenOut: chainOut.address,
            hubs: HUBS,
            amountIn: netAmountIn,
            signal: controller.signal
          }),
          fetchKyberQuote({ tokenIn, tokenOut, amountIn: netAmountIn, signal: controller.signal })
        ])
        if (cancelled || mine !== sequence.current) return

        /*
         * Through the router, KyberSwap's row is only offered when the router
         * will call KyberSwap's contract — the allowlist is the router's, not
         * ours to assume. A row that would fail at the dry run is not a row.
         */
        const kyberRoutable =
          kyber && routerAddress && kyber.kyber
            ? await isRouterTarget(provider, routerAddress, kyber.kyber.routerAddress)
            : Boolean(kyber)
        if (cancelled || mine !== sequence.current) return

        /*
         * The same rule applied to our own venues. Every one of them is a
         * `setTarget` away from being refused, and a venue the router will not
         * call must not be ranked first: it would open a wallet for a
         * transaction that reverts with `TargetNotAllowed`. Read once per venue
         * per session, and never at all when there is no router.
         */
        let routable = onChain.quotes
        if (routerAddress && onChain.quotes.length > 0) {
          const allowed = await readAllowedVenues(
            provider,
            routerAddress,
            onChain.quotes.map((q) => (q.candidate as RouteCandidate).venue)
          )
          if (cancelled || mine !== sequence.current) return
          routable = onChain.quotes.filter((q) => allowed.has((q.candidate as RouteCandidate).venue))
          /*
           * Dropping a venue silently is how a misconfiguration becomes a worse
           * price nobody notices: the page still quotes, still ranks, still
           * looks right, and the best pool on the chain is simply absent. So
           * the first time it happens in a session it is reported, with the
           * venue named — the allowlist is changed by a transaction only the
           * operator can send.
           */
          const dropped = [
            ...new Set(
              onChain.quotes
                .map((q) => (q.candidate as RouteCandidate).venue)
                .filter((venue) => !allowed.has(venue))
            )
          ]
          for (const venue of dropped) {
            if (reportedUnroutable.has(venue)) continue
            reportedUnroutable.add(venue)
            reportClientError({
              message: `swap.venueNotAllowlisted: ${venue} — the platform router refuses its target, so its routes are hidden`,
              kind: 'transaction',
              handled: true
            })
          }
        }

        const all = kyber && kyberRoutable ? [...routable, kyber] : routable
        const annotated = routerAddress
          ? all.map((q) => ({ ...q, grossAmountIn: amountIn, feeIn, feeBps: effectiveFeeBps }))
          : all
        setQuotes(rankQuotes(annotated))
        setQuotedAt(Date.now())
        setError(all.length === 0 ? 'noRoute' : 'none')
      } catch (err) {
        if (cancelled || mine !== sequence.current) return
        if ((err as { name?: string })?.name === 'AbortError') return
        /*
         * A multicall that threw is a chain we could not read; a multicall that
         * came back with only empty rows is a pair with no pool. They look the
         * same on screen unless the page says which, and the advice differs —
         * one is "try again", the other is "there is nothing here".
         */
        setQuotes([])
        setError('unavailable')
        /*
         * A page that cannot price anything is invisible otherwise.
         *
         * Every other swap failure reaches observability through
         * `decodeSwapError`, because it ends in a toast. This one does not: it
         * is a panel that quietly says it could not read the price, and a user
         * meeting it does not report a bug — they leave. Recorded as handled,
         * because we know exactly what it is and it is almost always the RPC
         * pool refusing.
         */
        reportClientError({
          message: `swap.quoteUnavailable: ${tokenIn.symbol}->${tokenOut.symbol} ${String(
            (err as { message?: unknown })?.message ?? err
          ).slice(0, 200)}`,
          kind: 'transaction',
          handled: true
        })
      } finally {
        if (!cancelled && mine === sequence.current) setLoading(false)
      }
    }

    const debounce = setTimeout(() => void run(), DEBOUNCE_MS)
    const interval = setInterval(() => void run(), REFRESH_MS)
    return () => {
      cancelled = true
      clearTimeout(debounce)
      clearInterval(interval)
    }
    // `tokenIn`/`tokenOut` are compared by address: a new object for the same
    // token must not restart a quote that is already correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    chainId,
    inAddress,
    outAddress,
    amountIn,
    account,
    mproAddress,
    masterDistributorAddress,
    routerAddress,
    nonce
  ])

  useEffect(() => {
    return () => {
      inFlight.current?.abort()
    }
  }, [])

  return {
    quotes,
    best: quotes[0] ?? null,
    loading,
    error,
    burnBps,
    feeBps,
    quotedAt,
    refresh
  }
}
